import React, { useState, useEffect } from "react";
import subscriptionRequests from "../../api/SubscriptionRequests";
import { toast } from "react-toastify";
import Loader from "../utils/Loader";
import { useLocation } from "react-router-dom";
import Navbar from "../dashboard/Navbar";

const PaymentHistory = () => {
  const location = useLocation();
  const userId = location.state?.userId;
  const [loading, setLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      setLoading(true);
      try {
        const response = await subscriptionRequests.getUserSubscriptionHistory(
          userId
        );
        if (response?.data?.status_code === "000") {
          setPaymentHistory(response?.data?.data);
        } else {
          toast.error(
            response?.data?.reason || "Error fetching payment history"
          );
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.data?.reason ||
            "Error fetching payment history"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchPaymentHistory();
  }, [userId]);

  // Filter and search functionality
  const filteredPayments = paymentHistory.filter((payment) => {
    const matchesSearch =
      payment.plan_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.reference_generated
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      payment.user?.full_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      payment.client_email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "paid" && payment.marked_as_paid === "1") ||
      (filterStatus === "failed" && payment.marked_as_paid === "0");

    return matchesSearch && matchesStatus;
  });

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format amount
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
    }).format(amount);
  };
  // new Items 

  // Get status badge
  const getStatusBadge = (markedAsPaid) => {
    if (markedAsPaid === "paid") {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Paid
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Failed
        </span>
      );
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <Navbar />
      <div className="mx-auto mt-10 max-w-8xl bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Header */}
        <div className="px-8 py-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                  Payment History
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-gray-600">
                  Track and manage all payment transactions, subscription
                  records, and billing information for your customers
                </p>
              </div>
            </div>
            <div className="mt-6 lg:mt-0 lg:ml-8">
              <div className="px-4 py-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">
                      {filteredPayments.length}
                    </div>
                    <div className="text-xs tracking-wide text-gray-500 uppercase">
                      Total Records
                    </div>
                  </div>
                  <div className="w-px h-8 bg-gray-300"></div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-red-600">
                      {
                        filteredPayments.filter((p) => p.marked_as_paid === "1")
                          .length
                      }
                    </div>
                    <div className="text-xs tracking-wide text-gray-500 uppercase">
                      Paid
                    </div>
                  </div>
                  <div className="w-px h-8 bg-gray-300"></div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-red-600">
                      {
                        filteredPayments.filter((p) => p.marked_as_paid === "0")
                          .length
                      }
                    </div>
                    <div className="text-xs tracking-wide text-gray-500 uppercase">
                      Failed
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-col gap-4 sm:flex-row">
            {/* Search */}
            <div className="flex-1">
              <label htmlFor="search" className="sr-only">
                Search payments
              </label>
              <div className="relative">
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  className="block py-2 pr-3 pl-10 w-full leading-5 placeholder-gray-500 bg-white rounded-md border border-gray-300 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search by plan, reference, name, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-48">
              <label htmlFor="status-filter" className="sr-only">
                Filter by status
              </label>
              <select
                id="status-filter"
                className="block px-3 py-2 w-full leading-5 bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Reference
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Customer
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Plan
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Issued Date
                </th>
                {/* <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                Due Date
              </th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg
                        className="mx-auto w-12 h-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No payments found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {searchTerm || filterStatus !== "all"
                          ? "Try adjusting your search or filter criteria."
                          : "No payment history available for this user."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {payment.reference_generated}
                      </div>
                      <div className="text-sm text-gray-500">
                        ID: {payment.user_id?.slice(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10">
                          <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-full">
                            <span className="text-sm font-medium text-blue-600">
                              {payment.user?.full_name
                                ?.charAt(0)
                                ?.toUpperCase() || "U"}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.user_email || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {payment.phone_number || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {payment.plan_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatAmount(payment.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(payment.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        {filteredPayments.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex space-x-6">
                <div>
                  <span className="text-sm font-medium text-gray-500">
                    Total Amount:
                  </span>
                  <span className="ml-2 text-sm font-semibold text-gray-900">
                    {formatAmount(
                      filteredPayments.reduce(
                        (sum, payment) => sum + payment.amount,
                        0
                      )
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">
                    Paid:
                  </span>
                  <span className="ml-2 text-sm font-semibold text-red-600">
                    {
                      filteredPayments.filter((p) => p.marked_as_paid === "1")
                        .length
                    }
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">
                    Failed:
                  </span>
                  <span className="ml-2 text-sm font-semibold text-red-600">
                    {
                      filteredPayments.filter((p) => p.marked_as_paid === "0")
                        .length
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
